// react-tooltip centers the card on its trigger and never clamps to the viewport, so
// the rightmost buttons of a bar pinned to the viewer's top-right pushed the card off
// screen once locales with long labels (FR/DE/RU run 33-45 characters) filled the full
// 208px. Shift by the overflow amount instead.
//
// The delta is applied to the position react-tooltip computed, not to an absolute
// coordinate: the card is `position: fixed`, and its containing block is the viewport
// or the transformed `.texture-selector` depending on breakpoint. A translation delta
// is identical in both spaces, so this stays correct either way.
//
// react-tooltip@4 calls overridePosition with two different argument orders
// (showTooltip vs updatePosition), so resolve the trigger/tooltip nodes and the
// placement by scanning the args rather than relying on fixed positions.
const VIEWPORT_MARGIN = 8
const ARROW_SHIFT_PROPERTY = '--viewer-tooltip-arrow-shift'
const PLACES = ['top', 'bottom', 'left', 'right']

// Only top/bottom cards sit centred on the trigger. For left/right, react-tooltip
// offsets the card clear of the trigger's edge (`l: -(tipWidth + targetWidth / 2 +
// triangleHeight)`), so the centred coordinate below would not describe where the card
// actually is and the resulting delta would move it somewhere arbitrary.
const CENTERED_PLACES = ['top', 'bottom']

export const keepTooltipInViewport = (position, ...rest) => {
  const nodes = rest.filter((arg) => arg && typeof arg.getBoundingClientRect === 'function')
  const tooltip = nodes.find((el) => el.classList && el.classList.contains('cgt-tooltip'))
  const trigger = nodes.find((el) => el !== tooltip)

  if (!tooltip || !trigger) {
    return position
  }

  // react-tooltip re-places a card that would fall outside the window, so the same
  // instance can show bottom once and left the next time. Reset the arrow on the
  // placements we do not clamp so a shift from an earlier show does not linger.
  const place = rest.find((arg) => PLACES.includes(arg))

  if (!CENTERED_PLACES.includes(place)) {
    tooltip.style.setProperty(ARROW_SHIFT_PROPERTY, '0px')

    return position
  }

  const { width } = tooltip.getBoundingClientRect()
  const triggerRect = trigger.getBoundingClientRect()
  const centered = triggerRect.left + (triggerRect.width / 2) - (width / 2)
  // Left margin wins when the card is wider than the viewport, so it is never
  // pushed off the left edge to satisfy the right one.
  const clamped = Math.max(VIEWPORT_MARGIN, Math.min(centered, window.innerWidth - width - VIEWPORT_MARGIN))
  const shift = clamped - centered

  // The card is centred on its trigger, so shifting it also moves the arrow away from
  // the icon. Feed the offset back so the arrow can be nudged in the other direction.
  // Always written, including 0, so a value from a previous show does not linger.
  tooltip.style.setProperty(ARROW_SHIFT_PROPERTY, `${-shift}px`)

  return { left: position.left + shift, top: position.top }
}

export default keepTooltipInViewport
